import { useRef } from 'react'
import { NetworkStatus } from '@apollo/client'
import { StoredFilterParams } from '@equipmentshare/dcf-search-filter-experience'
import { useDcAdminFulfillmentOrdersQuery, DcAdminFulfillmentOrdersQuery, FulfillmentOrderStatus } from '../../../graphql'
import { useDcAdminContext } from '../contexts/DcAdminContext'

export type FulfillmentOrderRow = NonNullable<DcAdminFulfillmentOrdersQuery['fulfillmentOrders']['edges'][number]['node']>

export type FulfillmentOrdersSearchRequest = {
	search?: string,
	statuses?: string,
	destinationInventoryIds?: string,
	orderNumbers?: string,
	demandRequestIds?: string,
	transferOrderIds?: string,
	dateCreatedAfter?: string,
	dateCreatedBefore?: string,
	page?: number,
	size?: number,
}

export default function useFulfillmentOrdersDataQuery(
	apiFilterParams: StoredFilterParams<FulfillmentOrdersSearchRequest>
) {
	const { selectedDcId } = useDcAdminContext()

	const size = apiFilterParams.size
		? Number( Array.isArray( apiFilterParams.size ) ? apiFilterParams.size[ 0 ] : apiFilterParams.size )
		: 25
	const page = apiFilterParams.page
		? Number( Array.isArray( apiFilterParams.page ) ? apiFilterParams.page[ 0 ] : apiFilterParams.page )
		: 0

	const statusesRaw = apiFilterParams.statuses
	const statuses = Array.isArray( statusesRaw )
		? statusesRaw as FulfillmentOrderStatus[]
		: statusesRaw ? [ statusesRaw as FulfillmentOrderStatus ] : undefined

	const destinationInventoryIdsRaw = apiFilterParams.destinationInventoryIds
	const destinationInventoryIds = Array.isArray( destinationInventoryIdsRaw )
		? destinationInventoryIdsRaw
		: destinationInventoryIdsRaw ? [ destinationInventoryIdsRaw ] : undefined

	const dateCreatedAfterRaw = apiFilterParams.dateCreatedAfter
	const dateCreatedAfter = Array.isArray( dateCreatedAfterRaw ) ? dateCreatedAfterRaw[ 0 ] : dateCreatedAfterRaw
	const dateCreatedBeforeRaw = apiFilterParams.dateCreatedBefore
	const dateCreatedBefore = Array.isArray( dateCreatedBeforeRaw ) ? dateCreatedBeforeRaw[ 0 ] : dateCreatedBeforeRaw

	const cursorsRef = useRef<( string | undefined )[]>( [ undefined ] )
	const prevSizeRef = useRef( size )
	const orderNumbersRaw = apiFilterParams.orderNumbers
	const orderNumbers = Array.isArray( orderNumbersRaw )
		? orderNumbersRaw.map( ( n ) => Number( n ) ).filter( ( n ) => !isNaN( n ) )
		: orderNumbersRaw ? [ Number( orderNumbersRaw ) ].filter( ( n ) => !isNaN( n ) ) : undefined

	const demandRequestIdsRaw = apiFilterParams.demandRequestIds
	const demandRequestIds = Array.isArray( demandRequestIdsRaw )
		? demandRequestIdsRaw
		: demandRequestIdsRaw ? [ demandRequestIdsRaw ] : undefined

	const transferOrderIdsRaw = apiFilterParams.transferOrderIds
	const transferOrderIds = Array.isArray( transferOrderIdsRaw )
		? transferOrderIdsRaw
		: transferOrderIdsRaw ? [ transferOrderIdsRaw ] : undefined

	const prevFiltersRef = useRef( JSON.stringify( { statusesRaw, destinationInventoryIdsRaw, orderNumbersRaw, demandRequestIdsRaw, transferOrderIdsRaw, dateCreatedAfterRaw, dateCreatedBeforeRaw, selectedDcId } ) )

	const filtersKey = JSON.stringify( { statusesRaw, destinationInventoryIdsRaw, orderNumbersRaw, demandRequestIdsRaw, transferOrderIdsRaw, dateCreatedAfterRaw, dateCreatedBeforeRaw, selectedDcId } )

	if (
		prevSizeRef.current !== size ||
		prevFiltersRef.current !== filtersKey
	) {
		cursorsRef.current = [ undefined ]
		prevSizeRef.current = size
		prevFiltersRef.current = filtersKey
	}

	const { data, previousData, networkStatus } = useDcAdminFulfillmentOrdersQuery( {
		variables: {
			fulfillingInventoryIds: selectedDcId ? [ selectedDcId ] : undefined,
			destinationInventoryIds,
			statuses,
			orderNumbers: orderNumbers?.length ? orderNumbers : undefined,
			demandRequestIds: demandRequestIds?.length ? demandRequestIds : undefined,
			transferOrderIds: transferOrderIds?.length ? transferOrderIds : undefined,
			dateCreatedAfter,
			dateCreatedBefore,
			paging: {
				first: size,
				after: cursorsRef.current[ page ],
			},
		},
		skip: !selectedDcId,
		fetchPolicy: 'network-only',
		notifyOnNetworkStatusChange: true,
	} )

	const displayData = data ?? previousData

	if ( displayData?.fulfillmentOrders.pageInfo.endCursor ) {
		cursorsRef.current[ page + 1 ] = displayData.fulfillmentOrders.pageInfo.endCursor
	}

	const rows = ( displayData?.fulfillmentOrders.edges ?? [] ).map( ( edge ) => edge.node )

	return {
		isFetching: networkStatus !== NetworkStatus.ready,
		data: displayData?.fulfillmentOrders
			? {
				rows,
				rowCount: displayData.fulfillmentOrders.totalCount,
			}
			: undefined,
	}
}
